import classNames from 'classnames';
import React from 'react';

// Tailwind color mapping to actual hex values
const TAILWIND_COLORS: Record<string, string> = {
  red: '#ef4444',
  orange: '#f97316',
  amber: '#f59e0b',
  yellow: '#eab308',
  green: '#22c55e',
  emerald: '#10b981',
  teal: '#14b8a6',
  cyan: '#06b6d4',
  sky: '#0ea5e9',
  blue: '#3b82f6',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  purple: '#a855f7',
  fuchsia: '#d946ef',
  pink: '#ec4899',
  rose: '#f43f5e',
};

// Get available color names from the colors object
const AVAILABLE_COLORS = Object.keys(TAILWIND_COLORS);

interface GradientAvatarProps {
  /**
   * Unique key used to deterministically generate the gradient.
   * The same key will always produce the same gradient combination.
   * Different keys will produce varied gradients with different:
   * - Colors (from a palette of 17 colors)
   * - Directions (top-left to bottom-right OR top-right to bottom-left)
   * - Color relationships (+2 or -2 positions in the color array)
   *
   * @example
   * gradientKey="user-123"     // Will always generate the same gradient
   * gradientKey="account-456"  // Will generate a different gradient
   */
  gradientKey: string;

  /**
   * Size of the avatar circle.
   *
   * @default 'md'
   * @example
   * size="sm"  // 24x24px (h-6 w-6)
   * size="md"  // 36x36px (h-9 w-9)
   * size="lg"  // 48x48px (h-12 w-12)
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Additional CSS classes to apply to the avatar container.
   * Useful for custom positioning, margins, or other styling.
   *
   * @example
   * className="mr-2 border-2 border-white"
   */
  className?: string;

  /**
   * Content to display inside the avatar circle.
   * Can be text, icons, emojis, or any React element.
   * Content will be centered and styled with white text.
   *
   * @example
   * {user.initials}        // "JD"
   * {"A"}                  // Single letter
   * {<UserIcon />}         // Icon component
   * {"🚀"}                 // Emoji
   */
  children: React.ReactNode;
}

/**
 * Generate deterministic gradient properties based on a key
 *
 * Uses different parts of a hash to ensure maximum variety while maintaining
 * deterministic behavior. The same key will always produce the same result.
 *
 * @param key - Unique string to generate gradient from
 * @returns Object containing gradient direction and color names
 *
 * @internal
 *
 * ## Algorithm Details
 * - **Hash Generation**: Uses bit-shifting hash for good distribution
 * - **Direction**: Uses bit 0 for gradient direction (2 options)
 * - **Starting Color**: Uses bits 1-16 for color index (17 options)
 * - **Color Offset**: Uses bits 17-32 for +2/-2 offset (2 options)
 * - **Wrapping**: Handles array boundary overflow in both directions
 *
 * This creates 68 unique combinations (2 × 17 × 2) before any repeats.
 */
const generateGradientProperties = (key: string) => {
  // Ensure key is a valid string
  if (!key || typeof key !== 'string' || key.length === 0) {
    key = 'default-user';
  }

  // Convert key to a numeric hash
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    const char = key.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Make hash positive
  hash = Math.abs(hash);

  // Use different parts of the hash for different decisions

  // Gradient direction: 0 = "to bottom right", 1 = "to bottom left"
  const direction = hash & 1;
  const gradientDirection = direction === 0 ? 'to bottom right' : 'to bottom left';

  // Starting color index (use bits 1-16)
  const firstIndex = (hash >> 1) % AVAILABLE_COLORS.length;

  // Second color offset: 0 = +2, 1 = -2 (use bits 17-32)
  const offsetDirection = (hash >> 16) & 1;
  const offset = offsetDirection === 0 ? 2 : -2;

  // Calculate second color index with wrapping
  let secondIndex = firstIndex + offset;

  // Handle wrapping for both positive and negative overflow
  if (secondIndex >= AVAILABLE_COLORS.length) {
    secondIndex = secondIndex - AVAILABLE_COLORS.length;
  } else if (secondIndex < 0) {
    secondIndex = AVAILABLE_COLORS.length + secondIndex;
  }

  return {
    direction: gradientDirection,
    fromColor: AVAILABLE_COLORS[firstIndex],
    toColor: AVAILABLE_COLORS[secondIndex],
  };
};

/**
 * GradientAvatar Component
 *
 * A flexible avatar component that generates beautiful, deterministic gradient backgrounds
 * based on a unique key. Perfect for user avatars, account identifiers, or any circular
 * display element that needs consistent visual identity.
 *
 * ## Features
 * - **Deterministic**: Same key always produces the same gradient
 * - **Varied**: 68 unique gradient combinations (17 colors × 2 directions × 2 offsets)
 * - **Flexible**: Accepts any content as children (text, icons, emojis)
 * - **Responsive**: Multiple size options with proper scaling
 * - **Accessible**: Includes descriptive titles for screen readers
 *
 * ## How It Works
 * The component uses a hash of the provided key to deterministically select:
 * 1. **Gradient Direction**: Either "to bottom right" or "to bottom left"
 * 2. **Starting Color**: One of 17 carefully chosen colors
 * 3. **Color Relationship**: Second color is either +2 or -2 positions away
 * 4. **Wrapping**: Colors wrap around the array boundaries for variety
 *
 * ## Usage Examples
 *
 * @example
 * // Basic user avatar with initials
 * <GradientAvatar gradientKey="user-123">
 *   JD
 * </GradientAvatar>
 *
 * @example
 * // Large project avatar with icon
 * <GradientAvatar gradientKey="project-456" size="lg" className="border-2">
 *   <ProjectIcon />
 * </GradientAvatar>
 *
 * @example
 * // Small team avatar with emoji
 * <GradientAvatar gradientKey="team-alpha" size="sm">
 *   🚀
 * </GradientAvatar>
 *
 * @example
 * // Account avatar (via AccountAvatar wrapper)
 * <AccountAvatar accountId="12345" accountName="Acme Corp" />
 */
const GradientAvatar: React.FC<GradientAvatarProps> = ({
  gradientKey,
  size = 'md',
  className,
  children,
}) => {
  const { direction, fromColor, toColor } = generateGradientProperties(gradientKey);

  // Size classes
  const sizeClasses = {
    sm: 'h-6 w-6 text-xs',
    md: 'h-9 w-9 text-sm',
    lg: 'h-12 w-12 text-base',
  };

  // Create gradient using CSS custom properties to avoid Tailwind purging issues
  const gradientStyle = {
    background: `linear-gradient(${direction}, ${TAILWIND_COLORS[fromColor]}, ${TAILWIND_COLORS[toColor]})`,
  };

  return (
    <div
      className={classNames(
        'flex items-center justify-center rounded-full font-medium text-white',
        sizeClasses[size],
        className
      )}
      style={gradientStyle}>
      {children}
    </div>
  );
};

export default GradientAvatar;
